import { useEffect, useRef, useState } from 'react';
import {
  callSheetKey, fetchCallSheet, localCallSheet, shortMeta,
  type CallSheet as CallSheetData, type CallSheetRequest, type TenantBundle,
} from '@ce/core';
import { trackColor } from '@ce/theme';
import { useTheme } from '../ThemeProvider';
import { btn, divider, emptyBox, kicker, t } from '../styles';
import { CardArc } from './Chrome';
import { PulseArc, Sheen } from './Ambient';

/**
 * The Call Sheet — a planned running order for the day, built from the
 * attendee's interests by a model call that happens on the CMS.
 *
 * Session facts (time, room, title, track) are always drawn from the bundle.
 * The model contributes an intro line and a one-line reason per pick, and
 * nothing else reaches the screen. See packages/core/src/itinerary.ts.
 */
export function CallSheet({
  bundle,
  cmsUrl,
  day,
  interests,
  saved,
  onOpenSchedule,
  onOpenSession,
  onToggleSave,
}: {
  bundle: TenantBundle;
  cmsUrl: string;
  day: string;
  interests: string[];
  saved: string[];
  onOpenSchedule: () => void;
  onOpenSession: (id: string) => void;
  onToggleSave: (id: string) => void;
}) {
  const theme = useTheme();
  const [sheet, setSheet] = useState<CallSheetData | null>(null);
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);
  const cache = useRef(new Map<string, CallSheetData>());

  const request: CallSheetRequest = { tenantId: bundle.tenantId, day, interests, saved };
  const key = callSheetKey(request);

  useEffect(() => {
    if (!interests.length) {
      setSheet(null);
      return;
    }

    // Same interests on the same day is the common case — a tab away and back
    // should not spend another model call.
    const cached = cache.current.get(key);
    if (cached && nonce === 0) {
      setSheet(cached);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    // Paint the local plan immediately, then upgrade it when the model answers.
    // The section is never empty and never blocks on the network.
    setSheet(localCallSheet(bundle, request));
    setLoading(true);

    fetchCallSheet(cmsUrl, bundle, request, fetch, controller.signal)
      .then((result) => {
        if (cancelled) return;
        cache.current.set(key, result);
        setSheet(result);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // `key` captures every input that changes the plan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, nonce]);

  if (!interests.length) {
    return (
      <>
        <SectionHeading bundle={bundle} loading={false} onReplan={null} />
        <div style={emptyBox}>{bundle.strings.callSheetEmpty}</div>
      </>
    );
  }

  return (
    <>
      <SectionHeading
        bundle={bundle}
        loading={loading}
        onReplan={() => setNonce((n) => n + 1)}
      />

      <div
        style={{
          borderRadius: 12,
          background: 'linear-gradient(150deg, var(--color-accent-900), var(--color-surface) 68%)',
          boxShadow: 'var(--shadow-sm)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Sheen delay={4.5} />
        <CardArc size={220} top={-90} right={-80} />
        <CardArc size={140} top={-46} right={-36} opacity={0.12} />
        <PulseArc size={140} top={-46} right={-36} opacity={0.26} delay={0.6} duration={7} />

        {sheet?.intro && (
          <div
            style={{
              padding: '15px 16px 0',
              font: '500 14px/1.45 var(--font-body)',
              letterSpacing: '-0.005em',
              color: t(0.82),
              position: 'relative',
            }}
          >
            {sheet.intro}
          </div>
        )}

        <div style={{ padding: '14px 16px 6px', position: 'relative' }}>
          {sheet?.picks.map((pick, i) => {
            const on = saved.includes(pick.session.id);
            return (
              <div
                key={pick.session.id}
                style={{
                  display: 'flex',
                  gap: 12,
                  paddingBottom: 14,
                  position: 'relative',
                }}
              >
                {/* The through-line: a rail connecting the day's stops. */}
                <div style={{ flex: 'none', width: 12, position: 'relative' }}>
                  <div
                    style={{
                      position: 'absolute',
                      left: 5,
                      top: 14,
                      bottom: i === sheet.picks.length - 1 ? '100%' : -2,
                      width: 1,
                      background: divider,
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 5,
                      width: 11,
                      height: 11,
                      borderRadius: '50%',
                      border: `1.5px solid ${trackColor(theme, pick.session.track)}`,
                      background: 'var(--color-bg)',
                    }}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => onOpenSession(pick.session.id)}
                  style={{
                    flex: 1, minWidth: 0, textAlign: 'left', border: 0, padding: 0,
                    background: 'transparent', color: 'var(--color-text)', cursor: 'pointer',
                  }}
                >
                  <div style={{ font: '400 10.5px/1 var(--font-mono)', color: t(0.5) }}>
                    {pick.session.t}
                  </div>
                  <div
                    style={{
                      font: '500 14.5px/1.3 var(--font-body)',
                      margin: '6px 0 4px',
                    }}
                  >
                    {pick.session.title}
                  </div>
                  <div style={{ fontSize: 11.5, color: t(0.48) }}>{shortMeta(pick.session)}</div>
                  {pick.reason && (
                    <div
                      style={{
                        fontSize: 11.5,
                        lineHeight: 1.5,
                        color: 'var(--color-accent-300)',
                        marginTop: 6,
                      }}
                    >
                      {pick.reason}
                    </div>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => onToggleSave(pick.session.id)}
                  aria-label={on ? `Unstar ${pick.session.title}` : `Star ${pick.session.title}`}
                  style={{
                    flex: 'none',
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    cursor: 'pointer',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 13,
                    border: `1px solid ${on ? 'var(--color-accent)' : divider}`,
                    background: on ? 'rgba(var(--color-accent-rgb), 0.2)' : 'transparent',
                    color: on ? 'var(--color-accent-300)' : t(0.45),
                  }}
                >
                  {on ? '★' : '☆'}
                </button>
              </div>
            );
          })}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '11px 16px',
            borderTop: `1px solid ${divider}`,
            position: 'relative',
          }}
        >
          <button
            type="button"
            onClick={() => sheet?.picks.forEach((p) => !saved.includes(p.session.id) && onToggleSave(p.session.id))}
            style={btn('primary', { fontSize: 12.5 })}
          >
            Star all
          </button>
          <button type="button" onClick={onOpenSchedule} style={btn('secondary', { fontSize: 12.5 })}>
            Full schedule
          </button>
        </div>
      </div>

      {sheet?.fallback && !loading && (
        <div style={{ fontSize: 11, lineHeight: 1.5, color: t(0.38), marginTop: 8 }}>
          {bundle.strings.callSheetError}
        </div>
      )}
    </>
  );
}

function SectionHeading({
  bundle,
  loading,
  onReplan,
}: {
  bundle: TenantBundle;
  loading: boolean;
  onReplan: (() => void) | null;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 10,
        margin: '26px 0 11px',
      }}
    >
      <div>
        <div style={kicker(11, 'var(--color-accent-300)', 0.14)}>{bundle.strings.callSheetTitle}</div>
        <div style={{ fontSize: 11, color: t(0.42), marginTop: 5 }}>
          {loading ? 'Planning your day…' : bundle.strings.callSheetKicker}
        </div>
      </div>
      {onReplan && (
        <button type="button" onClick={onReplan} disabled={loading} style={btn('ghost', { fontSize: 12 })}>
          Replan
        </button>
      )}
    </div>
  );
}
